import { createContext, useContext, useState, useEffect } from 'react';
import api from '@/utils/api';
import { getToken } from '@/utils/auth';

const SettingsContext = createContext();

const defaultSettings = {
  dateFormat: 'MM/DD/YYYY',
  currency: '$',
  theme: 'system',
  membershipDueReminder: 7, // days before
  thermalPrinter: {
    mode: 'none',
    usbVendorId: '',
    usbProductId: '',
    paired: false
  }
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('gymAppSettings');
    return saved ? JSON.parse(saved) : defaultSettings;
  });

  const fetchCompanySettings = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await api.get('/company/settings');
      const company = response.data;
      
      setSettings(prev => ({
        ...prev,
        dateFormat: company.date_format || prev.dateFormat,
        currency: company.currency || prev.currency,
        membershipDueReminder: company.membership_reminder_days || prev.membershipDueReminder
      }));
    } catch (error) {
      console.error("Failed to fetch company settings:", error);
    }
  };

  // Fetch company settings on mount
  useEffect(() => {
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    localStorage.setItem('gymAppSettings', JSON.stringify(settings));
    applyTheme(settings.theme);
  }, [settings]);

  const applyTheme = (theme) => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
      root.style.colorScheme = 'dark';
    } else if (theme === 'light') {
      root.classList.remove('dark');
      root.style.colorScheme = 'light';
    } else {
      // System theme
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark');
        root.style.colorScheme = 'dark';
      } else {
        root.classList.remove('dark');
        root.style.colorScheme = 'light';
      }
    }
  };

  const updateSettings = async (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));

    // Check if we need to update backend
    const backendUpdates = {};
    if (newSettings.dateFormat !== undefined) backendUpdates.date_format = newSettings.dateFormat;
    if (newSettings.currency !== undefined) backendUpdates.currency = newSettings.currency;
    if (newSettings.membershipDueReminder !== undefined) backendUpdates.membership_reminder_days = newSettings.membershipDueReminder;

    if (Object.keys(backendUpdates).length > 0) {
      const token = getToken();
      if (token) {
        try {
          await api.patch('/company/settings', backendUpdates);
        } catch (error) {
          console.error("Failed to save settings to backend:", error);
        }
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const format = settings.dateFormat;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    switch (format) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'YYYY/MM/DD':
        return `${year}/${month}/${day}`;
      case 'DD-MM-YYYY':
        return `${day}-${month}-${year}`;
      default:
        return date.toLocaleDateString();
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0).toFixed(2);
    return `${settings.currency}${num}`;
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, formatDate, formatCurrency, refreshSettings: fetchCompanySettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};
